import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ACCENT = "#4FA3D9";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shown once, right after first registration — asks ONLY for the person's name.
export default function NameCapture({ onDone }) {
  const { refresh } = useAuth();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setError("");
    if (!name.trim()) { setError("Please enter your name."); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/auth/name`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim() }),
      });
      if (!r.ok) throw new Error("failed");
      await refresh();
      if (onDone) onDone();
    } catch {
      setError("We couldn't save that. Please try again.");
    }
    setBusy(false);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="mx-auto max-w-md" data-testid="name-capture">
      <span className="text-[11px] font-medium uppercase tracking-[0.28em] text-white/50">Welcome to Adaptience&trade;</span>
      <h1 className="mb-2 mt-2 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">What should we call you?</h1>
      <p className="mb-8 text-sm leading-relaxed text-white/50">Just your name — nothing else needed.</p>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && save()}
        placeholder="Your name"
        autoFocus
        data-testid="name-input"
        className="w-full rounded-xl border border-white/12 bg-black/30 px-4 py-3 text-base text-white placeholder:text-white/30 focus:border-[#4FA3D9] focus:outline-none"
      />
      {error && <p className="mt-2 text-sm text-red-300/80" data-testid="name-error">{error}</p>}
      <button
        onClick={save}
        disabled={busy}
        data-testid="name-save-btn"
        className="mt-5 flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
        style={{ background: ACCENT }}
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} Continue
      </button>
    </motion.div>
  );
}
